import React, { useState, lazy } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { LOGO_URL, MAPS_URL, INSTAGRAM_URL } from '../data/constants';
import { getWhatsAppUrl } from '../utils/whatsapp';
import { Instagram, MessageCircle, MapPin, Heart, ChevronDown } from 'lucide-react';
import { LegalModal } from './LegalModal';
type LegalKey = 'cookies' | 'privacy' | 'terms' | 'dataProtection' | null;
type AccordionKey = 'links' | 'contact' | 'legal' | null;
export const Footer = () => {
  const {
    t
  } = useLanguage();
  const [openLegal, setOpenLegal] = useState<LegalKey>(null);
  const [openAccordion, setOpenAccordion] = useState<AccordionKey>(null);
  const handleWhatsApp = () => {
    const message = t('wa.help');
    window.open(getWhatsAppUrl(message), '_blank');
  };
  const legalLinks: {
    key: LegalKey;
    labelKey: string;
  }[] = [{
    key: 'cookies',
    labelKey: 'footer.cookies'
  }, {
    key: 'privacy',
    labelKey: 'footer.privacy'
  }, {
    key: 'terms',
    labelKey: 'footer.terms'
  }, {
    key: 'dataProtection',
    labelKey: 'footer.dataProtection'
  }];
  const toggle = (key: Exclude<AccordionKey, null>) => setOpenAccordion(openAccordion === key ? null : key);
  return <>
      <footer className="bg-[#001040] text-white/80 pt-10 sm:pt-14 md:pt-16 pb-6 sm:pb-8 border-t border-white/10" data-id="element-182" data-mp="3q8n125k1gf82">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8" data-id="element-183" data-mp="82idvwen2ggf">
          {/* Brand block — only on mobile, centered */}
          <div className="md:hidden text-center mb-8" data-id="element-184" data-mp="32w3rvu3xgbu">
            <img src={LOGO_URL} alt="Kevin Boats" loading="lazy" decoding="async" className="h-24 object-contain mb-3 mx-auto" data-id="element-185" data-mp="hsttwhblkg3o" />
            <p className="text-sm" data-id="element-186" data-mp="qt8h5m84g59t">Torrevieja · España</p>
            <p className="text-xs text-white/60 mt-2 max-w-xs mx-auto" data-id="element-187" data-mp="qtrne5oet580">
              {t('footer.desc')}
            </p>
          </div>

          {/* Mobile: stacked accordions */}
          <div className="md:hidden mt-8 space-y-2 border-t border-white/10 pt-2" data-id="element-188" data-mp="twfmjorqrnps">
            {/* Links */}
            <div className="border-b border-white/10" data-id="element-189" data-mp="yupoci7z1mkd">
              <button onClick={() => toggle('links')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-190" data-mp="13sr2coqjivoz">
                <span data-id="element-191" data-mp="1ckknypkxewlf">{t('footer.links')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'links' ? 'rotate-180' : ''}`} data-id="element-192" data-mp="1cnyr401ez9tx" />
              </button>
              {openAccordion === 'links' && <ul className="pb-4 space-y-3 text-sm" data-id="element-193" data-mp="1rilibf65p0dg">
                  <li data-id="element-194" data-mp="1ri3k4czxoc2w">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-195" data-mp="3ob53vv65on1f">
                      {t('fleet.filter.noLicense')}
                    </a>
                  </li>
                  <li data-id="element-196" data-mp="3tai2fd6e3iju">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-197" data-mp="3tc2umw58epej">
                      {t('fleet.filter.license')}
                    </a>
                  </li>
                  <li data-id="element-198" data-mp="25mu8apoogvoq">
                    <a href="#barcos" className="hover:text-turquoise" data-id="element-199" data-mp="25mke0pwlo2d8">
                      {t('fleet.filter.premium')}
                    </a>
                  </li>
                  <li data-id="element-200" data-mp="1wpa355np2f8u">
                    <a href="#motos" className="hover:text-turquoise" data-id="element-201" data-mp="1wlma07m5vuun">
                      {t('nav.jetski')}
                    </a>
                  </li>
                  <li data-id="element-202" data-mp="2pdfo9tinu697">
                    <a href="#licencias" className="hover:text-turquoise" data-id="element-203" data-mp="2pcw8ojghftyc">
                      {t('nav.licenses')}
                    </a>
                  </li>
                </ul>}
            </div>

            {/* Contact */}
            <div className="border-b border-white/10" data-id="element-204" data-mp="18xuxp40mfhtd">
              <button onClick={() => toggle('contact')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-205" data-mp="18zfrgrf42wq6">
                <span data-id="element-206" data-mp="2rtmx8fasqt6m">{t('footer.contact')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'contact' ? 'rotate-180' : ''}`} data-id="element-207" data-mp="2wmptwwaveir1" />
              </button>
              {openAccordion === 'contact' && <ul className="pb-4 space-y-4 text-sm" data-id="element-208" data-mp="2nue1j4w74l8f">
                  <li data-id="element-209" data-mp="3bj6y93kn6x43">
                    <button onClick={handleWhatsApp} className="flex items-center space-x-3 hover:text-turquoise" data-id="element-210" data-mp="3bl1lwpnat3bv">
                      <MessageCircle className="w-4 h-4 flex-shrink-0" data-id="element-211" data-mp="31mc2utjo1cu1" />
                      <span data-id="element-212" data-mp="36kdlxeh3dc5i">+34 692 489 818</span>
                    </button>
                  </li>
                  <li data-id="element-213" data-mp="1q6nfw26ewdt8">
                    <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-214" data-mp="1q647bc2nj1rv">
                      <Instagram className="w-4 h-4 flex-shrink-0" data-id="element-215" data-mp="1nnsig22mo97h" />
                      <span data-id="element-216" data-mp="1imcthyxbxijt">@kevin.boats</span>
                    </a>
                  </li>
                  <li data-id="element-217" data-mp="1wlcg711a7ndc">
                    <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-218" data-mp="1wkjjytzy8g9g">
                      <MapPin className="w-4 h-4 flex-shrink-0" data-id="element-219" data-mp="zx6a09opomwy" />
                      <span data-id="element-220" data-mp="1dnruu3xq76xh">Torrevieja, España</span>
                    </a>
                  </li>
                </ul>}
            </div>

            {/* Legal */}
            <div className="border-b border-white/10" data-id="element-221" data-mp="18uynr9esbmi8">
              <button onClick={() => toggle('legal')} className="w-full flex justify-between items-center py-4 text-white font-bold uppercase tracking-wider text-xs" data-id="element-222" data-mp="l6pbay3gob3r">
                <span data-id="element-223" data-mp="2g9ubptzk8dvu">{t('footer.legal')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${openAccordion === 'legal' ? 'rotate-180' : ''}`} data-id="element-224" data-mp="2l9qdrnop5v8r" />
              </button>
              {openAccordion === 'legal' && <ul className="pb-4 space-y-3 text-sm" data-id="element-225" data-mp="2uzt4tic9cz4g">
                  {legalLinks.map(link => <li key={link.key} data-id="element-226" data-mp="1xckmtxbgu877">
                      <button onClick={() => setOpenLegal(link.key)} className="hover:text-turquoise text-left" data-id="element-227" data-mp="1xdvtc4wapu20">
                        {t(link.labelKey)}
                      </button>
                    </li>)}
                </ul>}
            </div>
          </div>

          {/* Desktop: 4-column grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-12 mb-12" data-id="element-228" data-mp="3irgp3hf0lbxr">
            {/* Brand */}
            <div className="space-y-4" data-id="element-229" data-mp="1b3cjyemcnm90">
              <img src={LOGO_URL} alt="Kevin Boats" loading="lazy" decoding="async" className="h-28 object-contain mb-4" data-id="element-230" data-mp="1b2axzf3spfci" />
              <p className="text-sm" data-id="element-231" data-mp="129hdn39hrprs">Torrevieja · España</p>
              <p className="text-sm" data-id="element-232" data-mp="xch9wz046z3u">{t('footer.desc')}</p>
            </div>

            <div data-id="element-233" data-mp="scuuciwtd6rv">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-234" data-mp="sde2j7wnwgsp">
                {t('footer.links')}
              </h4>
              <ul className="space-y-3 text-sm" data-id="element-235" data-mp="nc8alcatsxk8">
                <li data-id="element-236" data-mp="jbwsa65xr9bx">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-237" data-mp="jd7sxx4340yt">
                    {t('fleet.filter.noLicense')}
                  </a>
                </li>
                <li data-id="element-238" data-mp="2dq7c207obdau">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-239" data-mp="2dqqtzgxbq1ov">
                    {t('fleet.filter.license')}
                  </a>
                </li>
                <li data-id="element-240" data-mp="28p1n8dh4wa6l">
                  <a href="#barcos" className="hover:text-turquoise" data-id="element-241" data-mp="1hm0juklcw3ox">
                    {t('fleet.filter.premium')}
                  </a>
                </li>
                <li data-id="element-242" data-mp="1mk20yzz81sxr">
                  <a href="#motos" className="hover:text-turquoise" data-id="element-243" data-mp="1mmo7cwadm2ee">
                    {t('nav.jetski')}
                  </a>
                </li>
                <li data-id="element-244" data-mp="1cu0eudqfiklh">
                  <a href="#licencias" className="hover:text-turquoise" data-id="element-245" data-mp="1cth2dcjjsoeu">
                    {t('nav.licenses')}
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-246" data-mp="12yfgqqohe4h0">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-247" data-mp="12urlnn3e1a06">
                {t('footer.contact')}
              </h4>
              <ul className="space-y-4 text-sm" data-id="element-248" data-mp="tvo4wprp16ou">
                <li data-id="element-249" data-mp="tya8yffdl42b">
                  <button onClick={handleWhatsApp} className="flex items-center space-x-3 hover:text-turquoise text-left" data-id="element-250" data-mp="2jb4ajv6esa9z">
                    <MessageCircle className="w-5 h-5" data-id="element-251" data-mp="2o84eo0jp72yg" />
                    <span data-id="element-252" data-mp="cnwv4getl2wo">+34 692 489 818</span>
                  </button>
                </li>
                <li data-id="element-253" data-mp="7otqgx2r4yp8">
                  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-254" data-mp="7pbvai3hcl8r">
                    <Instagram className="w-5 h-5" data-id="element-255" data-mp="vd377kl3yw9i" />
                    <span data-id="element-256" data-mp="vbs5dqb83yl1">@kevin.boats</span>
                  </a>
                </li>
                <li data-id="element-257" data-mp="qdgtla1dbe06">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 hover:text-turquoise" data-id="element-258" data-mp="qe0emzmbeiif">
                    <MapPin className="w-5 h-5" data-id="element-259" data-mp="372m8ycu0zk3k" />
                    <span data-id="element-260" data-mp="373fj7ns70rqg">Torrevieja, España</span>
                  </a>
                </li>
              </ul>
            </div>

            <div data-id="element-261" data-mp="3265optpy5pvr">
              <h4 className="text-white font-bold mb-6 uppercase tracking-wider text-sm" data-id="element-262" data-mp="itywp7labjsq">
                {t('footer.legal')}
              </h4>
              <ul className="space-y-3 text-sm" data-id="element-263" data-mp="dtkl5fkh5rpy">
                {legalLinks.map(link => <li key={link.key} data-id="element-264" data-mp="8wu8z3bsjg9t">
                    <button onClick={() => setOpenLegal(link.key)} className="hover:text-turquoise text-left" data-id="element-265" data-mp="8xvr1rr85311">
                      {t(link.labelKey)}
                    </button>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 sm:pt-8 mt-6 md:mt-0 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3 sm:gap-4 text-[11px] sm:text-xs text-white/50 text-center md:text-left" data-id="element-266" data-mp="rtc6k1dbmadz">
            <p className="leading-relaxed" data-id="element-267" data-mp="1kciwbvsyihb5">
              © {new Date().getFullYear()} Kevin Boats · Torrevieja · España
              <br className="sm:hidden" data-id="element-268" data-mp="1nt56v46djuti" />
              <span className="hidden sm:inline" data-id="element-269" data-mp="1nu6t84suc3qj"> · </span>
              {t('footer.rights')}
            </p>
            <p className="flex items-center space-x-1.5" data-id="element-270" data-mp="1rvicztp19uqt">
              <span data-id="element-271" data-mp="1wu3ahlcwi0bk">{t('footer.credit')}</span>
              <Heart className="w-3 h-3 text-turquoise fill-turquoise" data-id="element-272" data-mp="1ws8r4bha2g9l" />
              <a href="#" className="font-semibold text-white/70 hover:text-turquoise transition-colors" data-id="element-273" data-mp="21t6h3a8j4eln">
                KineticGrowthStudio
              </a>
            </p>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={openLegal === 'cookies'} onClose={() => setOpenLegal(null)} titleKey="legal.cookies.title" bodyKey="legal.cookies.body" data-id="element-274" data-mp="2fkbahulb0anj" />
      <LegalModal isOpen={openLegal === 'privacy'} onClose={() => setOpenLegal(null)} titleKey="legal.privacy.title" bodyKey="legal.privacy.body" data-id="element-275" data-mp="2kl8ri3ukuyng" />
      <LegalModal isOpen={openLegal === 'terms'} onClose={() => setOpenLegal(null)} titleKey="legal.terms.title" bodyKey="legal.terms.body" data-id="element-276" data-mp="2uhtwwqaiebrt" />
      <LegalModal isOpen={openLegal === 'dataProtection'} onClose={() => setOpenLegal(null)} titleKey="legal.dataProtection.title" bodyKey="legal.dataProtection.body" data-id="element-277" data-mp="1zvoghhyq4o3x" />
    </>;
};